import { memo } from "react";
import { Breadcrumb, PageHero } from "@";
import ProviderSelector from "./ProviderSelector";

/**
 * Shared layout chrome for each integration category page.
 * Renders breadcrumb -> hero -> provider selector -> form slot.
 *
 * @param {{
 *   eyebrow: string,
 *   title: string,
 *   icon: React.ComponentType<any>,
 *   selectorTitle?: string,
 *   selectorHint?: string,
 *   providers: Array<{ key: string, name: string, description?: string, logo?: string }>,
 *   active: string,
 *   onActiveChange: (key: string) => void,
 *   children?: React.ReactNode,
 *   breadcrumbName: string,
 *   isSaving?: boolean,
 * }} props
 */
const IntegrationShell = ({
  eyebrow,
  title,
  icon: Icon,
  selectorTitle = "اختر المزوّد",
  selectorHint = "يمكنك تفعيل مزوّد واحد فقط في كل مرة. سيُستخدم لكل العمليات تلقائياً.",
  providers,
  active,
  onActiveChange,
  children,
  breadcrumbName,
  isSaving,
}) => {
  const links = [
    { name: "الاعدادات", link: "/all-settings" },
    { name: "التكاملات", link: "/settings-integrations" },
    { name: breadcrumbName, link: "" },
  ];

  return (
    <>
      <Breadcrumb links={links} />

      <main className="table-card relative pb-5">
        <PageHero
          eyebrow={eyebrow}
          title={title}
          icon={Icon}
          count={providers.length}
          hideCount={false}
        />

        {/* Selector header */}
        <section className="mb-4 mt-2">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2 px-1">
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-gray-900 dark:text-gray-100">
                {selectorTitle}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {selectorHint}
              </p>
            </div>
            {isSaving && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2 py-1 text-[11px] font-semibold text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-purple-600" />
                جاري الحفظ
              </span>
            )}
          </div>

          <ProviderSelector
            providers={providers}
            value={active}
            onChange={onActiveChange}
            disabled={isSaving}
          />
        </section>

        {/* Form slot — appears below the selector */}
        {children}
      </main>
    </>
  );
};

export default memo(IntegrationShell);
